import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsletterService } from "../services/newsletterService";
import toast from "react-hot-toast";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error(t("footer.newsletter.error.empty"));
      return;
    }

    setIsLoading(true);

    try {
      const result = await newsletterService.subscribe(email);

      if (result.success) {
        toast.success(result.message || t("footer.newsletter.success"));
        setEmail("");
      } else {
        toast.error(result.message || t("footer.newsletter.error.generic"));
      }
    } catch (error) {
      toast.error(t("footer.newsletter.error.generic"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-white border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Section principale */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
          {/* Newsletter */}
          <div className="max-w-md">
            <h3 className="font-serif text-xl mb-3">
              {t("footer.newsletter.title")}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              {t("footer.newsletter.subtitle")}
            </p>
            <form onSubmit={handleSubmit} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.newsletter.placeholder")}
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 text-sm border border-gray-300 focus:border-black focus:ring-1 focus:ring-black disabled:opacity-50"
                required
              />
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2.5 bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {isLoading ? "..." : "OK"}
              </button>
            </form>
          </div>

          {/* Navigation */}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h4 className="font-medium text-sm mb-4">
                {t("footer.platform.title")}
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/pricing"
                    className="text-sm text-gray-600 hover:text-black transition-colors"
                  >
                    {t("footer.platform.pricing")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/insurance"
                    className="text-sm text-gray-600 hover:text-black transition-colors"
                  >
                    {t("footer.platform.insurance")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/fiches-pratiques"
                    className="text-sm text-gray-600 hover:text-black transition-colors"
                  >
                    {t("footer.platform.resources")}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-sm mb-4">
                {t("footer.info.title")}
              </h4>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-sm text-gray-600 hover:text-black transition-colors"
                  >
                    {t("footer.info.privacy")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/cookies"
                    className="text-sm text-gray-600 hover:text-black transition-colors"
                  >
                    {t("footer.info.cookies")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/legal"
                    className="text-sm text-gray-600 hover:text-black transition-colors"
                  >
                    {t("footer.info.legal")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/ai-transparency"
                    className="text-sm text-gray-600 hover:text-black transition-colors"
                  >
                    {t("footer.info.aiTransparency", { defaultValue: "IA & transparence" })}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bas de page */}
        <div className="pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Logo + copyright */}
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center space-x-2 group">
                <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center transition-transform group-hover:scale-105">
                  <span className="text-white font-bold text-lg">A</span>
                </div>
                <span className="text-xl font-serif font-bold tracking-tight">
                  ARTKIV
                </span>
              </Link>
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} {t("footer.copyright")}
              </p>
            </div>

            {/* Réseaux sociaux */}
            <div className="flex gap-3">
              {/* Instagram */}
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 border border-gray-300 hover:border-black hover:bg-black hover:text-white rounded-full flex items-center justify-center transition-all"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>

              {/* LinkedIn */}
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-9 h-9 border border-gray-300 hover:border-black hover:bg-black hover:text-white rounded-full flex items-center justify-center transition-all"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};